import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCustomerView } from '@/hooks/useCustomerView';
import { Bell, Eye, Printer, Package, Edit, GitMerge, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import axios from 'axios';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LAST_CHECK_KEY = 'adminOrdersLastCheck';
const SEEN_KEY = 'adminOrdersSeenNotifications';

const AdminOrdersButton = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [merging, setMerging] = useState(null);
  const pollIntervalRef = useRef(null);
  
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isCustomerView } = useCustomerView();
  const isAdminOrStaff = (user?.is_admin || user?.is_staff_user) && !isCustomerView;
  
  const calculateBasicPrice = (taxPaidPrice) => (taxPaidPrice || 0) / 1.18;
  
  // Fetch new orders
  const fetchOrders = useCallback(async () => {
    if (!token || !isAdminOrStaff) return;
    
    try {
      let lastCheck = localStorage.getItem(LAST_CHECK_KEY);
      if (!lastCheck) {
        lastCheck = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(); // Last 24 hours
      }
      
      const response = await axios.get(`${API}/admin/orders/new?since=${encodeURIComponent(lastCheck)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.orders) {
        const seen = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
        const newOrders = response.data.orders;
        
        // Count unseen orders
        const unseenCount = newOrders.filter(o => {
          const key = `${o.order_id}_${o.modified_at || o.last_edited_at || o.created_at}`;
          return !seen.includes(key);
        }).length;
        
        setOrders(newOrders);
        setUnreadCount(unseenCount);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  }, [token, isAdminOrStaff]);
  
  // Mark all as seen when popover opens
  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open && orders.length > 0) {
      const seen = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
      orders.forEach(o => {
        const key = `${o.order_id}_${o.modified_at || o.last_edited_at || o.created_at}`;
        if (!seen.includes(key)) seen.push(key);
      });
      // Keep only last 500
      if (seen.length > 500) seen.splice(0, seen.length - 500);
      localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
      setUnreadCount(0);
      localStorage.setItem(LAST_CHECK_KEY, new Date().toISOString());
    }
  };
  
  // Print order
  const printOrder = async (order) => {
    try {
      let fullOrder = order;
      if (!order.items || order.items.length === 0) {
        const response = await axios.get(`${API}/admin/orders/${order.order_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fullOrder = response.data;
      }
      
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.setTextColor(220, 38, 38);
      doc.text('JAI PARAS SPARES', 105, 15, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Order: ${fullOrder.order_id}`, 14, 28);
      doc.text(`Customer: ${fullOrder.customer_name || '-'}`, 14, 34);
      doc.text(`Company: ${fullOrder.company_name || '-'}`, 14, 40);
      
      // Dispatch mode and transporter
      const dispatchMode = fullOrder.dispatch_mode || 'transport';
      const dispatchModeLabel = dispatchMode === 'by_hand' ? 'By Hand' : 
                                dispatchMode === 'courier' ? 'Courier' : 
                                dispatchMode === 'local' ? 'Local Dispatch' : 'Transport';
      doc.text(`Dispatch: ${dispatchModeLabel}`, 14, 46);
      if (fullOrder.preferred_transporter && dispatchMode !== 'by_hand' && dispatchMode !== 'local') {
        doc.setFont(undefined, 'bold');
        doc.text(`Transporter: ${fullOrder.preferred_transporter}`, 14, 52);
        doc.setFont(undefined, 'normal');
      }
      
      doc.text(`Date: ${new Date(fullOrder.created_at).toLocaleDateString('en-IN', {  })}`, 150, 28);
      
      const tableStartY = fullOrder.preferred_transporter && dispatchMode !== 'by_hand' && dispatchMode !== 'local' ? 58 : 52;
      
      const tableData = fullOrder.items.map((item, index) => {
        const taxPaidUnitPrice = item.discounted_price || item.price || item.mrp || 0;
        const basicPricePerUnit = calculateBasicPrice(taxPaidUnitPrice);
        return [
          (index + 1).toString(),
          item.jp_part_no,
          item.part_name?.substring(0, 35) || '',
          item.quantity.toString(),
          basicPricePerUnit.toFixed(2),
          (basicPricePerUnit * item.quantity).toFixed(2)
        ];
      });

      autoTable(doc, {
        startY: tableStartY,
        head: [['#', 'Part No', 'Name', 'Qty', 'Price', 'Amount']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [220, 38, 38], fontSize: 9 },
        bodyStyles: { fontSize: 8 }
      });

      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) printWindow.onload = () => printWindow.print();
    } catch (error) {
      toast.error('Failed to print');
    }
  };
  
  // Merge orders
  const handleMerge = async (newOrderId, targetOrderId) => {
    setMerging(newOrderId);
    try {
      const response = await axios.post(`${API}/admin/orders/merge`, {
        new_order_id: newOrderId,
        target_order_id: targetOrderId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Orders merged into ${response.data.merged_order_id}`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge orders');
    } finally {
      setMerging(null);
    }
  };
  
  // View order
  const viewOrder = (orderId) => {
    setIsOpen(false);
    navigate(`/admin/orders?highlight=${orderId}`);
  };
  
  // Polling
  useEffect(() => {
    if (!isAdminOrStaff || !token) return;
    
    fetchOrders();
    pollIntervalRef.current = setInterval(fetchOrders, 30000);
    
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [isAdminOrStaff, token, fetchOrders]);
  
  if (!isAdminOrStaff) return null;
  
  // If no orders, just navigate to orders page on click
  if (orders.length === 0) {
    return (
      <Button
        variant="outline"
        className="relative rounded-md h-10 px-3"
        data-testid="admin-orders-notification-btn"
        onClick={() => navigate('/admin/orders')}
      >
        <Bell className="w-5 h-5" />
      </Button>
    );
  }
  
  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="relative rounded-md h-10 px-3"
          data-testid="admin-orders-notification-btn"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-2 -right-2 h-5 min-w-5 flex items-center justify-center p-0 bg-red-600 text-white text-xs rounded-full"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0 bg-white border-2 border-gray-200 shadow-2xl" align="end">
        <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Package className="w-4 h-4 text-red-600" />
              Recent Orders
            </h3>
            <p className="text-xs text-gray-500 mt-1">New and modified orders (last 24h)</p>
          </div>
          <Button
            variant="link"
            className="text-sm text-blue-600 hover:text-blue-800 p-0 h-auto font-semibold"
            onClick={() => {
              setIsOpen(false);
              navigate('/admin/orders');
            }}
          >
            View All Orders →
          </Button>
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {orders.slice(0, 10).map((order) => {
              const isNew = order.notification_type === 'new';
              const hasPending = order.has_pending_order;
              
              return (
                <div 
                  key={`${order.order_id}_${order.created_at}`}
                  className={`p-3 border-b border-gray-100 hover:bg-gray-50 ${isNew ? 'bg-red-50' : 'bg-orange-50'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {isNew ? (
                          <Package className="w-4 h-4 text-red-600 flex-shrink-0" />
                        ) : (
                          <Edit className="w-4 h-4 text-orange-600 flex-shrink-0" />
                        )}
                        <span className={`text-xs font-bold uppercase ${isNew ? 'text-red-600' : 'text-orange-600'}`}>
                          {isNew ? 'New Order' : 'Modified'}
                        </span>
                      </div>
                      <p className="font-semibold text-gray-900 truncate mt-1">
                        {order.customer_name || order.company_name || 'Customer'}
                      </p>
                      <p className="text-xs text-gray-500 font-mono">{order.order_id}</p>
                      {order.placed_by_tag && (
                        <p className="text-xs text-gray-500">Placed by: {order.placed_by_tag}{order.placed_by_mobile ? ` (${order.placed_by_mobile})` : ''}</p>
                      )}
                      {order.modified_by_tag && (
                        <p className="text-xs text-orange-500">Modified by: {order.modified_by_tag}</p>
                      )}
                      <p className="text-sm font-medium text-green-600">₹{order.final_amount?.toLocaleString('en-IN')}</p>
                    </div>
                    
                    <div className="flex flex-col gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-blue-600 hover:bg-blue-50"
                        onClick={() => viewOrder(order.order_id)}
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-purple-600 hover:bg-purple-50"
                        onClick={() => printOrder(order)}
                      >
                        <Printer className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Merge option - only for PENDING orders with another pending from same customer */}
                  {order.status === 'pending' && hasPending && order.pending_order_id !== order.order_id && (
                    <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded-md">
                      <p className="text-xs text-yellow-800 mb-2">
                        ⚠️ Same customer has pending order: <strong>{order.pending_order_id}</strong> (₹{order.pending_order_amount?.toLocaleString('en-IN')}, {order.pending_order_items} items)
                      </p>
                      <Button
                        size="sm"
                        className="w-full h-7 bg-yellow-600 hover:bg-yellow-700 text-white text-xs"
                        onClick={() => handleMerge(order.order_id, order.pending_order_id)}
                        disabled={merging === order.order_id}
                      >
                        {merging === order.order_id ? (
                          'Merging...'
                        ) : (
                          <>
                            <GitMerge className="w-3 h-3 mr-1" />
                            Merge into {order.pending_order_id}
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AdminOrdersButton;
